import { Box, Stack, Typography } from '@mui/material';
import React from 'react';

import { colors } from '../../theme/theme';
import { PLAIN_TEXT_RULES } from '../IMessageBubbleInterface';
import { capitalizeWithReplacements, highlightSupportWord } from '../MessageBubbleHelpers';

import { GuidanceCardShell, SoftRoundedCardShell } from './sharedCardHelpers';

const capitalizeFirstLetter = (str: string) => {
    if (!str || str.length === 0) {
        return str;
    }
    return capitalizeWithReplacements(str, PLAIN_TEXT_RULES);
};

// A block/line "looks like a heading" for narrative-block pairing when it ends with a colon,
// lacks terminal punctuation, contains a parenthetical, or opens with a known heading verb/phrase.
const isNarrativeHeadingLike = (value: string): boolean => {
    const trimmed = value.trim();

    // Ends with colon
    if (/:\s*$/.test(trimmed)) return true;

    // Lacks terminal punctuation
    if (!/[.!?]$/.test(trimmed)) return true;

    // Contains balanced parentheses (not vulnerable to catastrophic backtracking)
    if (trimmed.includes('(') && trimmed.includes(')') && trimmed.indexOf('(') < trimmed.lastIndexOf(')')) {
        return true;
    }

    // Starts with known heading phrase
    const headingPhrases = [
        'how',
        'signing',
        'updating',
        'contact',
        'payment',
        'contract',
        'please note',
        'to cancel',
        'to reschedule',
    ];
    const lowerValue = trimmed.toLowerCase();
    if (headingPhrases.some(phrase => lowerValue.startsWith(phrase))) {
        return true;
    }

    return false;
};

const toTrimmedNonEmptyLines = (block: string): string[] =>
    block
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean);

interface ContentSection {
    heading: string;
    items: string[];
    type: 'intro' | 'numbered' | 'bullet' | 'contact';
}

type ContentSectionBuilderState = {
    sections: ContentSection[];
    currentSection: ContentSection | null;
    introLines: string[];
};

const pushCurrentSection = (state: ContentSectionBuilderState) => {
    if (state.currentSection) {
        state.sections.push(state.currentSection);
        state.currentSection = null;
    }
};

const flushIntroLines = (state: ContentSectionBuilderState) => {
    if (state.introLines.length > 0 && state.sections.length === 0) {
        state.sections.push({ heading: '', items: state.introLines, type: 'intro' });
        state.introLines = [];
    }
};

const handleHeadingLine = (state: ContentSectionBuilderState, line: string) => {
    pushCurrentSection(state);
    flushIntroLines(state);
    state.currentSection = { heading: line.replace(/:\s*$/, ''), items: [], type: 'numbered' };
};

const handleNumberedLine = (state: ContentSectionBuilderState, line: string) => {
    if (!state.currentSection || state.currentSection.type === 'bullet') {
        pushCurrentSection(state);
        state.currentSection = { heading: '', items: [], type: 'numbered' };
    }
    state.currentSection.items.push(line.replace(/^\d{1,6}\.\s{1,20}/, ''));
};

const handleBulletLine = (state: ContentSectionBuilderState, line: string) => {
    if (!state.currentSection || state.currentSection.type === 'numbered') {
        pushCurrentSection(state);
        state.currentSection = { heading: '', items: [], type: 'bullet' };
    }
    state.currentSection.items.push(line.replace(/^[•\-*]\s{1,20}/, ''));
};

const handlePlainLine = (state: ContentSectionBuilderState, line: string) => {
    if (state.currentSection && state.currentSection.items.length > 0) {
        pushCurrentSection(state);
    }
    if (state.sections.length === 0) {
        state.introLines.push(line);
        return;
    }
    if (!state.currentSection) {
        state.currentSection = { heading: '', items: [], type: 'bullet' };
    }
    state.currentSection.items.push(line);
};

// Groups plain-text lines into headed sections by walking them once and classifying each line as
// a section heading, a numbered/bulleted list item, or plain text belonging to the current section
// (or the unheaded intro, before any section has started).
const buildContentSections = (lines: string[]): ContentSection[] => {
    const state: ContentSectionBuilderState = { sections: [], currentSection: null, introLines: [] };

    lines.forEach((line, idx) => {
        if (/:\s*$/.test(line) && idx > 0) {
            handleHeadingLine(state, line);
        } else if (/^\d{1,6}\.\s{1,20}/.test(line)) {
            handleNumberedLine(state, line);
        } else if (/^[•\-*]\s{1,20}/.test(line)) {
            handleBulletLine(state, line);
        } else {
            handlePlainLine(state, line);
        }
    });

    pushCurrentSection(state);
    if (state.introLines.length > 0) {
        state.sections.unshift({ heading: '', items: state.introLines, type: 'intro' });
    }

    return state.sections;
};

// Builds heading/body pairs from narrative blocks — an isolated heading-like block is paired with
// the next paragraph block; otherwise a block's own first line is used as the heading when it
// looks like one, or the whole block becomes an unheaded body.
const buildNarrativeSections = (blocks: string[]): Array<{ heading: string; body: string }> => {
    const sections: Array<{ heading: string; body: string }> = [];

    let blockIndex = 0;
    while (blockIndex < blocks.length) {
        const blockLines = toTrimmedNonEmptyLines(blocks[blockIndex]);
        if (!blockLines.length) {
            blockIndex += 1;
            continue;
        }

        // If a heading is isolated in its own block, pair it with the next paragraph block.
        if (blockLines.length === 1 && isNarrativeHeadingLike(blockLines[0]) && blockIndex + 1 < blocks.length) {
            const nextLines = toTrimmedNonEmptyLines(blocks[blockIndex + 1]);
            if (nextLines.length > 0) {
                sections.push({
                    heading: capitalizeFirstLetter(blockLines[0]),
                    body: capitalizeFirstLetter(nextLines.join(' ')),
                });
                blockIndex += 2;
                continue;
            }
        }

        if (blockLines.length === 1) {
            sections.push({ heading: '', body: capitalizeFirstLetter(blockLines[0]) });
            blockIndex += 1;
            continue;
        }

        const firstLine = blockLines[0];
        if (isNarrativeHeadingLike(firstLine)) {
            sections.push({
                heading: capitalizeFirstLetter(firstLine),
                body: capitalizeFirstLetter(blockLines.slice(1).join(' ')),
            });
            blockIndex += 1;
            continue;
        }

        sections.push({ heading: '', body: capitalizeFirstLetter(blockLines.join(' ')) });
        blockIndex += 1;
    }

    return sections;
};

type GuidanceItem =
    | { kind: 'numbered'; text: string; number: number }
    | { kind: 'roman'; text: string; numeral: string }
    | { kind: 'alpha'; text: string; letter: string }
    | { kind: 'bullet'; text: string }
    | { kind: 'text'; text: string };

const isNumberedGuidanceItem = (entry: GuidanceItem): entry is Extract<GuidanceItem, { kind: 'numbered' }> =>
    entry.kind === 'numbered';

const toAlphabeticMarker = (num: number) => {
    let value = Math.max(1, num);
    let marker = '';

    while (value > 0) {
        const remainder = (value - 1) % 26;
        marker = String.fromCharCode(97 + remainder) + marker;
        value = Math.floor((value - 1) / 26);
    }

    return marker || 'a';
};

const ROMAN_PAIRS: Array<[number, string]> = [
    [1000, 'm'],
    [900, 'cm'],
    [500, 'd'],
    [400, 'cd'],
    [100, 'c'],
    [90, 'xc'],
    [50, 'l'],
    [40, 'xl'],
    [10, 'x'],
    [9, 'ix'],
    [5, 'v'],
    [4, 'iv'],
    [1, 'i'],
];

const toRomanMarker = (num: number) => {
    let remaining = Math.max(1, num);
    let numeral = '';

    ROMAN_PAIRS.forEach(([value, symbol]) => {
        while (remaining >= value) {
            numeral += symbol;
            remaining -= value;
        }
    });

    return numeral || 'i';
};

// A nested bullet/roman block is one whose parent item (the item preceding the run of same-kind
// entries) ends its text with a colon — that signals "the following items belong to me".
const getNestedOrdinal = (orderedItems: GuidanceItem[], currentIndex: number, kind: GuidanceItem['kind']) => {
    if (orderedItems[currentIndex]?.kind !== kind) {
        return null;
    }

    let blockStartIndex = currentIndex;
    while (blockStartIndex > 0 && orderedItems[blockStartIndex - 1].kind === kind) {
        blockStartIndex -= 1;
    }

    const parentItem = orderedItems[blockStartIndex - 1];
    if (!parentItem || parentItem.kind === 'text' || parentItem.kind === kind) {
        return null;
    }

    if (!parentItem.text.trim().endsWith(':')) {
        return null;
    }

    return currentIndex - blockStartIndex + 1;
};

const getNestedBulletOrdinal = (orderedItems: GuidanceItem[], currentIndex: number) =>
    getNestedOrdinal(orderedItems, currentIndex, 'bullet');

const getNestedRomanOrdinal = (orderedItems: GuidanceItem[], currentIndex: number) =>
    getNestedOrdinal(orderedItems, currentIndex, 'roman');

const LIST_LAYOUT = {
    markerMinWidth: '24px',
    nestedMarkerMinWidth: '24px',
    topLevelGap: 0.9,
    nestedGap: 0.9,
    nestedIndent: 5.5,
};

// Bolds quoted substrings within instruction text, capitalizing the first letter inside the quotes.
const emphasizeInstructionText = (value: string): React.ReactNode => {
    const matcher = /"[^"]+"|'[^']+'/g;
    const output: React.ReactNode[] = [];
    let lastIndex = 0;
    let match = matcher.exec(value);

    while (match) {
        if (match.index > lastIndex) {
            output.push(value.slice(lastIndex, match.index));
        }

        output.push(
            <Box key={`${match[0]}-${match.index}`} component="span" sx={{ fontWeight: 700 }}>
                {capitalizeQuotedMatch(match[0])}
            </Box>,
        );

        lastIndex = match.index + match[0].length;
        match = matcher.exec(value);
    }

    if (lastIndex < value.length) {
        output.push(value.slice(lastIndex));
    }

    return output.length > 0 ? output : value;
};

const capitalizeQuotedMatch = (rawMatch: string) => {
    const quote = rawMatch[0];
    const inner = rawMatch.slice(1, -1).trim();
    if (!inner) {
        return rawMatch;
    }
    const firstLetterIndex = inner.search(/[a-z]/i);
    if (firstLetterIndex === -1) {
        return `${quote}${inner}${quote}`;
    }
    const normalizedInner =
        inner.slice(0, firstLetterIndex) +
        inner.charAt(firstLetterIndex).toUpperCase() +
        inner.slice(firstLetterIndex + 1);
    return `${quote}${normalizedInner}${quote}`;
};

// Shared "item text" rendering used by every guided-list marker branch (numbered/roman/bullet/alpha) —
// identical in every branch except for the line-height, which differs slightly by list kind.
const renderGuidedItemText = (text: string, lineHeight: number) => (
    <Typography variant="body2" sx={{ color: '#1F2937', lineHeight, fontSize: '0.95rem' }}>
        {emphasizeInstructionText(capitalizeFirstLetter(text))}
    </Typography>
);

const NumberedGuidedItem: React.FC<{
    item: Extract<GuidanceItem, { kind: 'numbered' }>;
    useAlphabeticTopLevel: boolean;
}> = ({ item, useAlphabeticTopLevel }) => (
    <Stack direction="row" alignItems="flex-start" gap={LIST_LAYOUT.topLevelGap}>
        <Typography
            variant="body2"
            sx={{
                color: colors.fullBlack,
                fontWeight: 800,
                fontSize: '0.92rem',
                minWidth: LIST_LAYOUT.markerMinWidth,
                lineHeight: 1.45,
            }}
        >
            {useAlphabeticTopLevel ? toAlphabeticMarker(item.number) : item.number}.
        </Typography>
        {renderGuidedItemText(item.text, 1.45)}
    </Stack>
);

const RomanGuidedItem: React.FC<{ item: Extract<GuidanceItem, { kind: 'roman' }>; isNested: boolean }> = ({
    item,
    isNested,
}) => (
    <Stack
        direction="row"
        alignItems="flex-start"
        gap={isNested ? LIST_LAYOUT.nestedGap : LIST_LAYOUT.topLevelGap}
        sx={{ pl: isNested ? LIST_LAYOUT.nestedIndent : 0 }}
    >
        <Typography
            variant="body2"
            sx={{
                color: colors.fullBlack,
                fontWeight: 700,
                fontSize: '0.92rem',
                minWidth: isNested ? LIST_LAYOUT.nestedMarkerMinWidth : LIST_LAYOUT.markerMinWidth,
                lineHeight: 1.45,
            }}
        >
            {item.numeral}.
        </Typography>
        {renderGuidedItemText(item.text, 1.45)}
    </Stack>
);

const NestedBulletGuidedItem: React.FC<{ item: Extract<GuidanceItem, { kind: 'bullet' }>; ordinal: number }> = ({
    item,
    ordinal,
}) => (
    <Stack direction="row" alignItems="flex-start" gap={LIST_LAYOUT.nestedGap} sx={{ pl: LIST_LAYOUT.nestedIndent }}>
        <Typography
            variant="body2"
            sx={{
                color: colors.fullBlack,
                fontWeight: 700,
                fontSize: '0.92rem',
                minWidth: LIST_LAYOUT.nestedMarkerMinWidth,
                lineHeight: 1.45,
            }}
        >
            {toRomanMarker(ordinal)}.
        </Typography>
        {renderGuidedItemText(item.text, 1.5)}
    </Stack>
);

const TopLevelBulletGuidedItem: React.FC<{ item: Extract<GuidanceItem, { kind: 'bullet' }> }> = ({ item }) => (
    <Stack direction="row" gap={1} alignItems="flex-start" sx={{ pl: 0 }}>
        <Box
            sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: colors.greyPreset[50],
                mt: 0.75,
                flexShrink: 0,
            }}
        />
        {renderGuidedItemText(item.text, 1.5)}
    </Stack>
);

const BulletGuidedItem: React.FC<{ item: Extract<GuidanceItem, { kind: 'bullet' }>; nestedOrdinal: number | null }> = ({
    item,
    nestedOrdinal,
}) =>
    nestedOrdinal ? (
        <NestedBulletGuidedItem item={item} ordinal={nestedOrdinal} />
    ) : (
        <TopLevelBulletGuidedItem item={item} />
    );

const AlphaGuidedItem: React.FC<{ item: Extract<GuidanceItem, { kind: 'alpha' }> }> = ({ item }) => (
    <Stack direction="row" alignItems="flex-start" gap={LIST_LAYOUT.topLevelGap} sx={{ pl: 0 }}>
        <Typography
            variant="body2"
            sx={{
                color: colors.fullBlack,
                fontWeight: 700,
                fontSize: '0.92rem',
                minWidth: LIST_LAYOUT.markerMinWidth,
                lineHeight: 1.45,
            }}
        >
            {item.letter}.
        </Typography>
        {renderGuidedItemText(item.text, 1.45)}
    </Stack>
);

const TextGuidedItem: React.FC<{ item: Extract<GuidanceItem, { kind: 'text' }>; isFirst: boolean }> = ({
    item,
    isFirst,
}) => {
    const shouldShowDivider =
        !isFirst && /(if you have further questions|our team will be happy to help)/i.test(item.text);
    const shouldAddTopSpacingBeforeContactLead =
        /(if you have questions about specific contract terms|customer service team in one of three ways)/i.test(
            item.text,
        );

    return (
        <Stack gap={0.45} sx={{ mt: shouldAddTopSpacingBeforeContactLead ? 0.65 : 0 }}>
            {shouldShowDivider && <Box sx={{ borderTop: `1px solid ${colors.greyPreset[30]}`, mt: 1.5, mb: 1.5 }} />}
            <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.45, fontSize: '0.95rem' }}>
                {highlightSupportWord(capitalizeFirstLetter(item.text))}
            </Typography>
        </Stack>
    );
};

const GuidedListItem: React.FC<{
    item: GuidanceItem;
    index: number;
    orderedItems: GuidanceItem[];
    useAlphabeticTopLevel: boolean;
}> = ({ item, index, orderedItems, useAlphabeticTopLevel }) => {
    switch (item.kind) {
        case 'numbered':
            return <NumberedGuidedItem item={item} useAlphabeticTopLevel={useAlphabeticTopLevel} />;
        case 'roman':
            return <RomanGuidedItem item={item} isNested={Boolean(getNestedRomanOrdinal(orderedItems, index))} />;
        case 'bullet':
            return <BulletGuidedItem item={item} nestedOrdinal={getNestedBulletOrdinal(orderedItems, index)} />;
        case 'alpha':
            return <AlphaGuidedItem item={item} />;
        default:
            return <TextGuidedItem item={item} isFirst={index === 0} />;
    }
};

const resolveHeadingLinesSource = (
    lines: string[],
    firstInstructionIndex: number,
    shouldAutoNumberPlainInstructions: boolean,
): string[] => {
    if (shouldAutoNumberPlainInstructions) {
        return [];
    }
    if (firstInstructionIndex === -1) {
        return lines;
    }
    return lines.slice(0, firstInstructionIndex);
};

const resolveGuidanceBodyLines = (
    lines: string[],
    firstInstructionIndex: number,
    shouldAutoNumberPlainInstructions: boolean,
    plainInstructionCandidates: string[],
): string[] => {
    if (firstInstructionIndex !== -1) {
        return lines.slice(firstInstructionIndex);
    }
    return shouldAutoNumberPlainInstructions ? plainInstructionCandidates : [];
};

const splitGuidanceLeadLine = (line: string): string[] => {
    // Asterisk runs are capped at 50 (no real markdown line has more) so a pathologically long
    // run of '*' can't force unbounded regex work — matching stays bounded regardless of input size.
    const cleanedLine = line
        .replace(/^\*{1,50}/, '')
        .replace(/\*{1,50}$/, '')
        .trim();
    if (!cleanedLine) {
        return [];
    }

    const pleaseNoteIndex = cleanedLine.toLowerCase().indexOf('please note');
    if (pleaseNoteIndex > 0) {
        return [cleanedLine.slice(0, pleaseNoteIndex).trim(), cleanedLine.slice(pleaseNoteIndex).trim()].filter(
            Boolean,
        );
    }

    const toCancelIndex = cleanedLine.toLowerCase().indexOf('to cancel');
    if (toCancelIndex > 0) {
        return [cleanedLine.slice(0, toCancelIndex).trim(), cleanedLine.slice(toCancelIndex).trim()].filter(Boolean);
    }

    // Normalize combined lead text like:
    // "... portal. to request changes, contact ..."
    const sentenceSplit = cleanedLine.match(/^([^.]{0,500}\.)\s{1,20}(to\s{1,20}\b.{0,500})$/i);
    if (sentenceSplit) {
        const firstPart = sentenceSplit[1].trim();
        const secondRaw = sentenceSplit[2].trim();
        const secondPartBase = secondRaw.charAt(0).toUpperCase() + secondRaw.slice(1);
        const isToDoSo = secondPartBase.toLowerCase().startsWith('to do so');
        const secondPart = isToDoSo ? `${secondPartBase.trimEnd().replace(/[.\s]+$/, '')}:` : secondPartBase;
        return [firstPart, secondPart].filter(Boolean);
    }

    return [cleanedLine];
};

const normalizeForCompare = (value: string) => value.toLowerCase().replace(/[.:]/g, '').replace(/\s+/g, ' ').trim();

const dedupeAdjacentHeadingLines = (rawLines: string[]): string[] =>
    rawLines.filter((line, index, arr) => {
        if (index === 0) {
            return true;
        }

        const currentNormalized = normalizeForCompare(line);
        const previousNormalized = normalizeForCompare(arr[index - 1]);
        const firstNormalized = normalizeForCompare(arr[0]);

        if (!currentNormalized || currentNormalized === previousNormalized) {
            return false;
        }

        return !(firstNormalized === currentNormalized || firstNormalized.includes(currentNormalized));
    });

const isGuidanceInstructionLine = (line: string) =>
    /^\d{1,6}\.\s{1,20}/.test(line) ||
    /^[a-z]\.\s{1,20}/i.test(line) ||
    /^[ivxlcdm]{1,20}\.\s{1,20}/i.test(line) ||
    /^[•\-*]\s{1,20}/.test(line);

const GUIDANCE_INSTRUCTION_VERB =
    /^(on the|go to|click|next click|select|enter|add|submit|navigate|open|choose|provide|fill|attach|review|confirm|describe)\b/i;

const shouldEmphasizeGuidanceLeadLine = (value: string) =>
    /(for security reasons|cannot be displayed|cannot display|steps provided below|please follow these steps)/i.test(
        value,
    );

const appendToPreviousListItem = (orderedItems: GuidanceItem[], lineText: string): boolean => {
    const lastItem = orderedItems[orderedItems.length - 1];
    if (!lastItem) {
        return false;
    }

    const normalizedLine = lineText.trim();
    const looksLikeContinuation = /^(business\s+hours\s*:|hours\s*:)/i.test(normalizedLine);
    if (!looksLikeContinuation) {
        return false;
    }

    if (lastItem.kind === 'numbered' || lastItem.kind === 'alpha' || lastItem.kind === 'roman') {
        lastItem.text = `${lastItem.text} ${normalizedLine}`.trim();
        return true;
    }

    return false;
};

const classifyRomanCandidate = (
    orderedItems: GuidanceItem[],
    romanMatch: RegExpMatchArray,
): { numeral: string; text: string } | null => {
    const romanMarker = romanMatch[1].toLowerCase();
    const isSingleRomanChar = romanMarker.length === 1;
    const previousItem = orderedItems[orderedItems.length - 1];
    const previousStartsNestedList =
        !!previousItem &&
        (previousItem.kind === 'numbered' ||
            previousItem.kind === 'alpha' ||
            previousItem.kind === 'roman' ||
            previousItem.kind === 'bullet') &&
        previousItem.text.trim().endsWith(':');
    const continuesRomanSequence = previousItem?.kind === 'roman';

    // Keep single-letter markers (e.g. "c.") in the alpha branch unless clearly nested/continuing.
    if (isSingleRomanChar && !previousStartsNestedList && !continuesRomanSequence) {
        return null;
    }

    return { numeral: romanMarker, text: romanMatch[2].trim() };
};

const classifyGuidanceLine = (
    orderedItems: GuidanceItem[],
    line: string,
    shouldAutoNumberPlainInstructions: boolean,
): GuidanceItem => {
    // Marker digits and separating whitespace are bounded (no real numbered-list marker needs more
    // than 6 digits or 20 spaces) so a pathological input can't force unbounded regex work.
    const numberedMatch = line.match(/^(\d{1,6})\.\s{1,20}(.*)$/);
    if (numberedMatch) {
        return { kind: 'numbered', number: Number(numberedMatch[1]), text: numberedMatch[2].trim() };
    }

    const romanMatch = line.match(/^([ivxlcdm]{1,20})\.\s{1,20}(.*)$/i);
    const romanItem = romanMatch ? classifyRomanCandidate(orderedItems, romanMatch) : null;
    if (romanItem) {
        return { kind: 'roman', ...romanItem };
    }

    const alphaMatch = line.match(/^([a-z])\.\s{1,20}(.*)$/i);
    if (alphaMatch) {
        return { kind: 'alpha', letter: alphaMatch[1].toLowerCase(), text: alphaMatch[2].trim() };
    }

    const bulletMatch = line.match(/^[•\-*]\s{1,20}(.*)$/);
    if (bulletMatch) {
        return { kind: 'bullet', text: bulletMatch[1].trim() };
    }

    if (shouldAutoNumberPlainInstructions) {
        return { kind: 'numbered', number: orderedItems.filter(isNumberedGuidanceItem).length + 1, text: line };
    }

    return { kind: 'text', text: line };
};

const buildOrderedGuidanceItems = (bodyLines: string[], shouldAutoNumberPlainInstructions: boolean): GuidanceItem[] => {
    const orderedItems: GuidanceItem[] = [];

    bodyLines.forEach(line => {
        if (appendToPreviousListItem(orderedItems, line)) {
            return;
        }
        orderedItems.push(classifyGuidanceLine(orderedItems, line, shouldAutoNumberPlainInstructions));
    });

    return orderedItems;
};

const buildGuidanceHeadingLines = (
    lines: string[],
    firstInstructionIndex: number,
    shouldAutoNumberPlainInstructions: boolean,
): string[] => {
    const headingLinesSource = resolveHeadingLinesSource(
        lines,
        firstInstructionIndex,
        shouldAutoNumberPlainInstructions,
    );
    const explicitLeadLines =
        firstInstructionIndex > 0
            ? lines
                  .slice(0, firstInstructionIndex)
                  .map(line => line.trim())
                  .filter(Boolean)
            : [];

    const headingLinesRaw = headingLinesSource.flatMap(splitGuidanceLeadLine).filter(Boolean);
    const explicitLeadCandidates = explicitLeadLines.flatMap(splitGuidanceLeadLine).filter(Boolean);

    explicitLeadCandidates.forEach(explicitLeadLine => {
        const normalizedLead = normalizeForCompare(explicitLeadLine);
        const alreadyPresent = headingLinesRaw.some(line => normalizeForCompare(line) === normalizedLead);
        if (!alreadyPresent) {
            headingLinesRaw.push(explicitLeadLine);
        }
    });

    return dedupeAdjacentHeadingLines(headingLinesRaw);
};

// Renders the step-by-step / guided-list card (numbered, roman-numeral, alpha, and bullet
// markers, plus any lead/heading lines before the first list marker). Extracted out of
// PlainTextResponseCard so this branch's own control flow doesn't add to that component's
// cognitive complexity.
const GuidanceListCard: React.FC<{
    text: string;
    lines: string[];
    normalizedText: string;
    looksLikeInstructionWorkflow: boolean;
    isSupportWorkflowMessage: boolean;
}> = ({ text, lines, normalizedText, looksLikeInstructionWorkflow, isSupportWorkflowMessage }) => {
    const firstInstructionIndex = lines.findIndex(isGuidanceInstructionLine);
    const plainInstructionCandidates =
        firstInstructionIndex === -1
            ? lines
                  .map(line =>
                      line
                          .replace(/^\*{1,50}/, '')
                          .replace(/\*{1,50}$/, '')
                          .trim(),
                  )
                  .filter(Boolean)
            : [];
    const instructionLikeCount = plainInstructionCandidates.filter(line => GUIDANCE_INSTRUCTION_VERB.test(line)).length;
    const shouldAutoNumberPlainInstructions =
        firstInstructionIndex === -1 &&
        plainInstructionCandidates.length >= 3 &&
        instructionLikeCount >= 2 &&
        (looksLikeInstructionWorkflow ||
            isSupportWorkflowMessage ||
            normalizedText.includes('how to') ||
            normalizedText.includes('submit') ||
            normalizedText.includes('steps'));

    const isContractSupportCompositeMessage =
        /if you have questions about specific contract terms/i.test(text) &&
        /customer service team in one of three ways/i.test(text);

    const headingLines = buildGuidanceHeadingLines(lines, firstInstructionIndex, shouldAutoNumberPlainInstructions);
    const bodyLines = resolveGuidanceBodyLines(
        lines,
        firstInstructionIndex,
        shouldAutoNumberPlainInstructions,
        plainInstructionCandidates,
    );
    const orderedItems = buildOrderedGuidanceItems(bodyLines, shouldAutoNumberPlainInstructions);
    const useAlphabeticTopLevel = orderedItems.some(item => item.kind === 'numbered');

    return (
        <GuidanceCardShell>
            <Stack gap={1}>
                <Stack gap={0.35} sx={{ minWidth: 0 }}>
                    {headingLines.map((line, index) => (
                        <Typography
                            key={index}
                            variant="body2"
                            sx={{ color: '#1F2937', lineHeight: 1.45, fontSize: '0.94rem' }}
                        >
                            {index === 0 &&
                            shouldEmphasizeGuidanceLeadLine(line) &&
                            !isContractSupportCompositeMessage ? (
                                <Box
                                    component="span"
                                    sx={{
                                        color: colors.fullBlack,
                                        fontWeight: 800,
                                        lineHeight: 1.35,
                                        fontSize: '1.02rem',
                                    }}
                                >
                                    {highlightSupportWord(capitalizeFirstLetter(line))}
                                </Box>
                            ) : (
                                highlightSupportWord(capitalizeFirstLetter(line))
                            )}
                        </Typography>
                    ))}
                </Stack>

                {orderedItems.map((item, index) => (
                    <GuidedListItem
                        key={`guided-item-${index}`}
                        item={item}
                        index={index}
                        orderedItems={orderedItems}
                        useAlphabeticTopLevel={useAlphabeticTopLevel}
                    />
                ))}
            </Stack>
        </GuidanceCardShell>
    );
};

export const PlainTextResponseCard: React.FC<{ text: string }> = ({ text }) => {
    const toAlphabetListLabel = (position: number) => {
        let value = Math.max(1, position);
        let label = '';

        while (value > 0) {
            const remainder = (value - 1) % 26;
            label = String.fromCharCode(97 + remainder) + label;
            value = Math.floor((value - 1) / 26);
        }

        return label || 'a';
    };

    const parseProfileSummaryResponse = (rawText: string) => {
        const lines = rawText
            .split('\n')
            .map(line => line.trim())
            .filter(Boolean);

        const heading = lines.find(line => /^here\s+is\s+a\s+summary\s+of\s+your\s+profile\s*:?$/i.test(line));
        if (!heading) {
            return null;
        }

        const summaryItems = lines
            .map(line => {
                // All quantifiers bounded, and no `\s*` directly before `(.*)` (it would overlap
                // with `.*`, both matching spaces) — value is trimmed below instead.
                const match = line.match(
                    /^\d{1,6}\.\s{0,20}(open\s{1,5}invoices|active\s{1,5}contracts|support\s{1,5}requests)\s{0,20}:(.*)$/i,
                );
                if (!match) {
                    return null;
                }
                const label = capitalizeFirstLetter(match[1].trim());
                const value = (match[2] || '').trim();
                return { label, value };
            })
            .filter((item): item is { label: string; value: string } => Boolean(item));

        if (!summaryItems.length) {
            return null;
        }

        const hasSpecificQuestion = lines.some(line =>
            /is\s+there\s+anything\s+specific\s+you\s+would\s+like\s+to\s+know\s+more\s+about\?/i.test(line),
        );

        return {
            heading: capitalizeFirstLetter(heading),
            summaryItems,
            followUp: hasSpecificQuestion ? 'If you want details on any specific area, please let me know!' : null,
        };
    };

    const parseNarrativeBlocks = (rawText: string) => {
        const normalizedNarrative = rawText.trim();
        if (!normalizedNarrative) {
            return null;
        }

        const blocks = normalizedNarrative
            .split(/\n\s*\n/)
            .map(block => block.trim())
            .filter(Boolean);

        if (blocks.length < 2) {
            return null;
        }

        const listMarkerPattern = /^\s{0,20}(\d{1,6}\.|[a-z]\.\s{1,20}|[ivxlcdm]{1,20}\.)/im;
        const hasListMarkers = blocks.some(block => listMarkerPattern.test(block));
        if (hasListMarkers) {
            return null;
        }

        const sections = buildNarrativeSections(blocks);
        return sections.length >= 2 ? sections : null;
    };

    const normalizedStructuredText = text
        .replace(/\s{1,20}\*\s{1,20}(?=\S)/g, '\n* ')
        .replace(/\n{3,10}/g, '\n\n')
        .trim();

    const profileSummary = parseProfileSummaryResponse(normalizedStructuredText);
    if (profileSummary) {
        return (
            <SoftRoundedCardShell>
                <Stack gap={0.9}>
                    <Typography variant="body2" sx={{ color: colors.fullBlack, fontWeight: 700, lineHeight: 1.45 }}>
                        {profileSummary.heading}
                    </Typography>

                    <Stack gap={0.35}>
                        {profileSummary.summaryItems.map(item => (
                            <Typography
                                key={item.label}
                                variant="body2"
                                sx={{ color: colors.fullBlack, lineHeight: 1.5 }}
                            >
                                {`${item.label}: ${item.value}`.trim()}
                            </Typography>
                        ))}
                    </Stack>

                    {profileSummary.followUp && (
                        <Typography variant="body2" sx={{ color: colors.fullBlack, lineHeight: 1.5, mt: 0.2 }}>
                            {profileSummary.followUp}
                        </Typography>
                    )}
                </Stack>
            </SoftRoundedCardShell>
        );
    }

    const narrativeSections = parseNarrativeBlocks(normalizedStructuredText);
    if (narrativeSections) {
        return (
            <SoftRoundedCardShell>
                <Stack gap={1.15}>
                    {narrativeSections.map((section, index) => (
                        <Box key={`narrative-${index}`}>
                            {section.heading && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: colors.fullBlack,
                                        fontWeight: 800,
                                        fontSize: '1rem',
                                        lineHeight: 1.35,
                                        mb: 0.4,
                                    }}
                                >
                                    {highlightSupportWord(section.heading)}
                                </Typography>
                            )}
                            <Typography
                                variant="body2"
                                sx={{
                                    color: colors.greyPreset[60],
                                    lineHeight: 1.55,
                                    fontSize: '0.95rem',
                                }}
                            >
                                {highlightSupportWord(section.body)}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            </SoftRoundedCardShell>
        );
    }
    const lines = normalizedStructuredText
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean);

    const sections = buildContentSections(lines);

    // Detect contact section
    for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (
            section.items.some(item => /email|@|call|phone|\d{3}-\d{3}-\d{4}|business hours/.test(item.toLowerCase()))
        ) {
            section.type = 'contact';
            break;
        }
    }

    const normalizedText = text.toLowerCase();
    const looksLikeUnknownResponse =
        /(i\s+(do not|don't)\s+(have|see|find|access))|\b(unable|cannot|can't|not available|not able)\b|\bi'?m\s+sorry\b/.test(
            normalizedText,
        );
    const hasContactSection = sections.some(section => section.type === 'contact' && section.items.length > 0);
    const hasSupportSection = sections.some(section => /support request/i.test(section.heading));
    const hasNumberedSection = sections.some(section => section.type === 'numbered' && section.items.length > 0);
    const hasAlphaOrRomanList = lines.some(line => /^([a-z]|[ivxlcdm]{1,20})\.\s{1,20}/i.test(line));
    const isSupportWorkflowMessage =
        /(support request|support requests|support ticket|new request)/i.test(text) ||
        normalizedText.includes('submit a new support request');
    const instructionLikeVerbForGuidance =
        /^(on the|go to|click|next click|select|enter|add|submit|navigate|open|choose|provide|fill|attach|review|confirm|describe)\b/i;
    const plainInstructionLineCount = lines.filter(line => instructionLikeVerbForGuidance.test(line)).length;
    const looksLikeInstructionWorkflow = lines.length >= 3 && plainInstructionLineCount >= 2;
    const hasSecurityPhrase =
        normalizedText.includes('for security reasons') ||
        normalizedText.includes('cannot be displayed here') ||
        normalizedText.includes('please follow these steps');
    const hasViewHeading = sections.some(section => /(view|access)[^\n]*(bank|document)/i.test(section.heading));
    const shouldRenderGuidanceCard =
        hasSecurityPhrase ||
        hasViewHeading ||
        looksLikeInstructionWorkflow ||
        hasNumberedSection ||
        hasAlphaOrRomanList ||
        (looksLikeUnknownResponse && (hasContactSection || hasSupportSection)) ||
        (isSupportWorkflowMessage && hasNumberedSection);

    // Shared section-heading rendering used by both the numbered and bullet section renderers below.
    const renderSectionHeading = (heading?: string) =>
        heading ? (
            <Typography variant="body2" sx={{ color: colors.fullBlack, fontWeight: 700, fontSize: '0.95rem' }}>
                {highlightSupportWord(capitalizeFirstLetter(heading))}
            </Typography>
        ) : null;

    if (shouldRenderGuidanceCard) {
        return (
            <GuidanceListCard
                text={text}
                lines={lines}
                normalizedText={normalizedText}
                looksLikeInstructionWorkflow={looksLikeInstructionWorkflow}
                isSupportWorkflowMessage={isSupportWorkflowMessage}
            />
        );
    }

    return (
        // Unified card chrome — identical to the guidance/steps card so a plain conversational
        // answer (e.g. "Click on 'Forgot password' ...") and a stepped answer render the same way,
        // driven by content shape rather than the specific question asked.
        <GuidanceCardShell>
            <Stack gap={1.2}>
                {sections.map((section, sectionIdx) => {
                    // Intro section
                    if (section.type === 'intro') {
                        return (
                            <Stack key={`intro-${sectionIdx}`} gap={0.6}>
                                {section.items.map((item, idx) => (
                                    <Typography
                                        key={`intro-${idx}`}
                                        variant="body2"
                                        sx={{
                                            color: colors.fullBlack,
                                            lineHeight: 1.6,
                                            fontSize: '0.9rem',
                                            fontWeight: idx === 0 ? 700 : 400,
                                        }}
                                    >
                                        {highlightSupportWord(capitalizeFirstLetter(item))}
                                    </Typography>
                                ))}
                            </Stack>
                        );
                    }

                    // Contact section
                    if (section.type === 'contact') {
                        return (
                            <Stack key={`contact-${sectionIdx}`} gap={0.7}>
                                {section.items.map((item, itemIdx) => (
                                    <Box
                                        key={`contact-${itemIdx}`}
                                        sx={{
                                            backgroundColor: colors.greyPreset[90],
                                            p: 1,
                                            borderRadius: '8px',
                                            border: `1px solid ${colors.greyPreset[30]}`,
                                        }}
                                    >
                                        {item.split('\n').map((line, lineIdx) => (
                                            <Typography
                                                key={`contact-text-${lineIdx}`}
                                                variant="body2"
                                                sx={{
                                                    color: colors.fullBlack,
                                                    fontSize: '0.9rem',
                                                    fontWeight:
                                                        line.includes('@') || /\d{3}-\d{3}-\d{4}/.test(line)
                                                            ? 600
                                                            : 500,
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                {highlightSupportWord(capitalizeFirstLetter(line))}
                                            </Typography>
                                        ))}
                                    </Box>
                                ))}
                            </Stack>
                        );
                    }

                    // Numbered section
                    if (section.type === 'numbered') {
                        return (
                            <Stack key={`numbered-${sectionIdx}`} gap={0.8}>
                                {renderSectionHeading(section.heading)}
                                <Stack gap={0.6}>
                                    {section.items.map((item, itemIdx) => (
                                        <Box
                                            key={`num-${itemIdx}`}
                                            sx={{
                                                display: 'flex',
                                                gap: 0.8,
                                                backgroundColor: colors.greyPreset[90],
                                                p: 0.9,
                                                borderRadius: '8px',
                                                border: `1px solid ${colors.greyPreset[30]}`,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: colors.fullBlack,
                                                    fontWeight: 700,
                                                    fontSize: '0.9rem',
                                                    minWidth: '24px',
                                                }}
                                            >
                                                {toAlphabetListLabel(itemIdx + 1)}.
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: colors.fullBlack,
                                                    lineHeight: 1.5,
                                                    fontSize: '0.9rem',
                                                }}
                                            >
                                                {highlightSupportWord(capitalizeFirstLetter(item))}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Stack>
                        );
                    }

                    // Bullet section
                    if (section.type === 'bullet') {
                        return (
                            <Stack key={`bullet-${sectionIdx}`} gap={0.8}>
                                {renderSectionHeading(section.heading)}
                                <Stack gap={0.5}>
                                    {section.items.map((item, itemIdx) => (
                                        <Stack
                                            key={`bullet-${itemIdx}`}
                                            direction="row"
                                            gap={0.8}
                                            alignItems="flex-start"
                                        >
                                            <Box
                                                sx={{
                                                    width: '6px',
                                                    height: '6px',
                                                    borderRadius: '50%',
                                                    backgroundColor: colors.fullBlack,
                                                    mt: 0.9,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: colors.fullBlack,
                                                    lineHeight: 1.5,
                                                    fontSize: '0.9rem',
                                                }}
                                            >
                                                {highlightSupportWord(capitalizeFirstLetter(item))}
                                            </Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </Stack>
                        );
                    }

                    return null;
                })}
            </Stack>
        </GuidanceCardShell>
    );
};
